/**
 * The text a user sees in the chat when a question fails (TF-19).
 *
 * The old text named the engine, a port and the engine logs. An expired session (401) ended in the text "Please
 * make sure the IE-PAL-Engine is running". Each case below says what the user can do next.
 *
 * It reads the shape axios gives an error (`response.status`, `code`, `message`) and does not import axios.
 */
type ErrorLike = {
  code?: string;
  message?: string;
  response?: { status?: number };
};

const SIGN_IN_AGAIN = 'Your session has expired. Please reload the page and sign in again.';
const SERVICE_PROBLEM = 'The service had a problem answering. Please try again in a moment.';
const NOT_AVAILABLE = 'This service is not available right now. Please try again later.';
const TOO_MANY = 'Too many requests right now. Please wait a moment and try again.';
const TOOK_TOO_LONG = 'The answer took too long. Please try again, or ask a narrower question.';
const CANNOT_REACH = 'Cannot reach the service. Please check your connection and try again.';
const REFUSED =
  'The service does not accept this question, for example because it has markup or special characters, or repeats the same words. Please reword it and ask again.';
const UNKNOWN = 'Something went wrong while answering your question. Please try again.';

export function chatErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return UNKNOWN;
  }
  const { code, message, response } = error as ErrorLike;
  const status = response?.status;

  if (status === 401 || status === 403) return SIGN_IN_AGAIN;
  if (status === 400) return REFUSED;
  if (status === 404) return NOT_AVAILABLE;
  if (status === 429) return TOO_MANY;
  if (status !== undefined && status >= 500) return SERVICE_PROBLEM;
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timeout/i.test(message ?? '')) return TOOK_TOO_LONG;
  if (code === 'ERR_NETWORK' || /Network Error/i.test(message ?? '')) return CANNOT_REACH;
  return UNKNOWN;
}
